import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressMembershipV2PlansPlansServiceV2Url(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/plan-proxy',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    '*.pub.wix-code.com': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    '*.wixapps.net': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    _: [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'editor-flow.wixapps.net': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'forums._base_domain_': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'forums.wixapps.net': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
      {
        srcPath: '/_api/pricing-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/paid-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/pricing-plans/v2/plans',
        destPath: '/v2/plans',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_pricing-plans_plans';

/**
 * Retrieves a list of up to 100 public pricing plans. Public plans are visible plans that are available to site visitors and can be purchased.
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.QueryPlans(), and will be removed on 2025-12-31.
 */
export function listPublicPlans(payload: object): RequestOptionsFactory<any> {
  function __listPublicPlans({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.membership.v2.plans.PlansServiceV2.ListPublicPlans',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans/public',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'plans.createdDate' },
              { path: 'plans.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listPublicPlans;
}

/**
 * Creates a query to retrieve a list of public pricing plans.
 *
 *
 * The `queryPublicPlans()` method builds a query to retrieve a list of up to 1,000 public plans and returns a [`PublicPlansQueryBuilder`](#plansquerybuilder) object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](#plansquerybuilder/find) method.
 *
 * You can refine the query by chaining `PublicPlansQueryBuilder` methods onto the query. `PublicPlansQueryBuilder` methods enable you to sort, filter, and control the results that `queryPublicPlans()` returns.
 *
 * `queryPublicPlans()` runs with the following `PublicPlansQueryBuilder` defaults that you can override:
 * - [`skip`](#plansquerybuilder/skip): `0`
 * - [`limit`](#plansquerybuilder/limit): `50`
 *
 * The methods that are chained to `queryPublicPlans()` are applied in the order they are called. For example, if you sort on the `_createdDate` property in ascending order and then on the `_id` property in ascending order, the results are sorted first by the created date of the items and then, if there are multiple results with the same date, the items are sorted by `_id` in ascending order, per created date value.
 *
 * The following `PublicPlansQueryBuilder` methods are supported for the `queryPublicPlans()` method. For a full description of the Plans object, see the object returned for the [`items`](#plansqueryresult/items) property in [`PublicPlansQueryResult`](#plansqueryresult).
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.QueryPlans(), and will be removed on 2025-12-31.
 */
export function queryPublicPlans(payload: object): RequestOptionsFactory<any> {
  function __queryPublicPlans({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.membership.v2.plans.PlansServiceV2.QueryPublicPlans',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans/public/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'plans.createdDate' },
              { path: 'plans.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryPublicPlans;
}

/**
 * Retrieves a pricing plan by ID.
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.GetPlan(), and will be removed on 2025-12-31.
 */
export function getPlan(payload: object): RequestOptionsFactory<any> {
  function __getPlan({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.membership.v2.plans.PlansServiceV2.GetPlan',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'plan.createdDate' }, { path: 'plan.updatedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __getPlan;
}

/**
 * Retrieves a list of up to 100 pricing plans (including public, hidden, and archived plans).
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.SearchPlans(), and will be removed on 2025-12-31.
 */
export function listPlans(payload: object): RequestOptionsFactory<any> {
  function __listPlans({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.membership.v2.plans.PlansServiceV2.ListPlans',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'plans.createdDate' },
              { path: 'plans.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listPlans;
}

/**
 * Gets statistics about the pricing plans. Currently provides only the total number of pricing plans, including archived plans.
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.CountPlans(), and will be removed on 2025-12-31.
 */
export function getPlanStats(payload: object): RequestOptionsFactory<any> {
  function __getPlanStats({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.membership.v2.plans.PlansServiceV2.GetPlanStats',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans/stats',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getPlanStats;
}

/**
 * Creates a pricing plan. The specified `plan` object must contain a pricing model. A pricing model can be 1 of the following:
 * - **A subscription**: A subscription with recurring payments and how often the plan occurs. Subscriptions can have free trial days.
 * - **A plan that doesn't renew**: A single payment for a specific duration that doesn't renew.
 * - **An unlimited plan**: A single payment for an unlimited amount of time (until canceled).
 * Pricing plans are available to the Wix user in the Pricing Plans section in a site's dashboard.
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.CreatePlan(), and will be removed on 2025-12-31.
 */
export function createPlan(payload: object): RequestOptionsFactory<any> {
  function __createPlan({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'plan.createdDate' }, { path: 'plan.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.membership.v2.plans.PlansServiceV2.CreatePlan',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'plan.createdDate' }, { path: 'plan.updatedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __createPlan;
}

/**
 * Updates a pricing plan.
 * Updating a plan doesn't impact existing orders made for the plan. All orders keep the details of the original plan that was active at the time of purchase.
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.UpdatePlan(), and will be removed on 2025-12-31.
 */
export function updatePlan(payload: object): RequestOptionsFactory<any> {
  function __updatePlan({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'plan.createdDate' }, { path: 'plan.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.membership.v2.plans.PlansServiceV2.UpdatePlan',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans/{plan.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'plan.createdDate' }, { path: 'plan.updatedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __updatePlan;
}

/**
 * Sets visibility for pricing plans. Visible plans are considered public plans.
 * By default, pricing plans are public, meaning they are visible. Plans can be hidden so that site members and visitors can't choose them.
 * As opposed to archiving, setting visibility can be reversed. This means that a public plan can be hidden, and a hidden plan can be made public (visible). (An archived plan always remains archived and can't be made active again.)
 * Changing a plan’s visibility doesn't impact existing orders for the plan. All orders for hidden plans are still active and keep their perks.
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.UpdatePlan(), and will be removed on 2025-12-31.
 */
export function setPlanVisibility(payload: object): RequestOptionsFactory<any> {
  function __setPlanVisibility({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'PUT' as any,
      methodFqn:
        'com.wixpress.membership.v2.plans.PlansServiceV2.SetPlanVisibility',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans/{id}/visibility',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'plan.createdDate' }, { path: 'plan.updatedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __setPlanVisibility;
}

/**
 * Marks a pricing plan as the primary pricing plan. When viewing pricing plans on a site, the primary plan is highlighted with a customizable ribbon. Only a single plan can be marked as a primary plan at any given time. If there is an existing plan marked as primary, calling Make Plan Primary causes the existing primary plan to lose its primary status.
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.BulkUpdatePlans(), and will be removed on 2025-12-31.
 */
export function makePlanPrimary(payload: object): RequestOptionsFactory<any> {
  function __makePlanPrimary({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.membership.v2.plans.PlansServiceV2.MakePlanPrimary',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans/{id}/make-primary',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'plan.createdDate' }, { path: 'plan.updatedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __makePlanPrimary;
}

/**
 * Sets all pricing plans as not primary. When viewing pricing plans on a site, no plan is highlighted with a customizable ribbon.
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.BulkUpdatePlans(), and will be removed on 2025-12-31.
 */
export function clearPrimary(payload: object): RequestOptionsFactory<any> {
  function __clearPrimary({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.membership.v2.plans.PlansServiceV2.ClearPrimary',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans/clear-primary',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __clearPrimary;
}

/**
 * Archives a single plan.
 * When a plan is archived, it's no longer visible as a public plan that can be chosen by site members or visitors.
 * This is because the plan's `public` property is automatically set to `false`.
 * Archived plans can't be purchased or reactivated.
 * Plan archiving doesn't impact existing orders made for the plan. All orders for the plan are still active and keep their perks, payment options, and terms.
 * Wix users can see archived plans in a site's dashboard under **Pricing Plans -> Archived Plans**.
 * > **Note:** An attempt to archive an already-archived plan throws an error.
 * @deprecated It has been replaced with wix.pricing_plans.plans.v3.PlanService.UpdatePlan(), and will be removed on 2025-12-31.
 */
export function archivePlan(payload: object): RequestOptionsFactory<any> {
  function __archivePlan({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.v2.plan',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.membership.v2.plans.PlansServiceV2.ArchivePlan',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembershipV2PlansPlansServiceV2Url({
        protoPath: '/v2/plans/{id}/archive',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'plan.createdDate' }, { path: 'plan.updatedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __archivePlan;
}
